import produce, { Draft } from 'immer';
import { unzip, zip } from 'lodash';

import { KeywordsEditorAction, KeywordsEditorActionType } from '../actions/keywordsEditor';
import {
    getInputStringFromPhrasesList,
    getKeywordsEditorInitialData,
    getKeywordsEditorInitialState,
    getUniqJoinedKeywordsData,
    getUniqKeywordsData,
    parseKeywordString,
    parsePhrasesFromInputString,
    parsePhrasesFromStringList
} from '../service/keywordsEditor';
import { KeywordsData, KeywordsEditorsState } from '../typings/keywordsEditorData';

export const keywordsEditorReducer = (
    state: KeywordsEditorsState = getKeywordsEditorInitialState(),
    action: KeywordsEditorAction
): KeywordsEditorsState => {
    return produce(state, (draftState: Draft<KeywordsEditorsState>) => {
        if (
            (action.type === KeywordsEditorActionType.OPEN || action.type === KeywordsEditorActionType.SET_ERRORS) &&
            !draftState.editors[action.editorId]
        ) {
            // нужно проинитить эдитор в случае ошибки, чтобы было откуда ее брать в закрытом виде
            // данные не пропадут, так как при открытии они берутся из группы
            draftState.editors[action.editorId] = getKeywordsEditorInitialData();
        }

        const editor = draftState.editors[action.editorId];
        if (!editor) {
            return;
        }

        switch (action.type) {
            case KeywordsEditorActionType.OPEN: {
                editor.value = action.keywords;
                editor.input = getInputStringFromPhrasesList(action.keywords, editor.view);

                editor.isOpen = true;
                break;
            }

            case KeywordsEditorActionType.UPDATE: {
                const keywordsData = parsePhrasesFromStringList(action.keywords);
                const newListKeywords = getUniqJoinedKeywordsData(editor.value, keywordsData);

                editor.value = newListKeywords;
                editor.input = getInputStringFromPhrasesList(newListKeywords, editor.view);
                editor.isStatLoading = true;

                break;
            }

            case KeywordsEditorActionType.CHANGE: {
                if (editor.input !== action.value) {
                    editor.errors = [];
                }
                editor.input = action.value;
                break;
            }

            case KeywordsEditorActionType.APPLY: {
                const keywordsData = parsePhrasesFromInputString(editor.input);
                const newListKeywords = getUniqKeywordsData(editor.value, keywordsData);

                editor.value = newListKeywords;
                editor.input = getInputStringFromPhrasesList(newListKeywords, editor.view);
                editor.isStatLoading = true;

                break;
            }

            case KeywordsEditorActionType.CLOSE: {
                editor.isOpen = false;

                break;
            }

            case KeywordsEditorActionType.SWITCH_TAB: {
                editor.selectedTabIndex = action.tabIndex;

                break;
            }

            case KeywordsEditorActionType.CHANGE_VIEW: {
                editor.input = getInputStringFromPhrasesList(editor.value, action.view);
                editor.view = action.view;

                break;
            }

            case KeywordsEditorActionType.FETCH_STAT: {
                editor.isStatLoading = true;

                break;
            }

            case KeywordsEditorActionType.UPDATE_STAT: {
                editor.isStatLoading = false;

                const [stat, keywords] = unzip(
                    zip(action.statData, editor.value).sort(([stat1], [stat2]) =>
                        (stat1 || 0) >= (stat2 || 0) ? -1 : 1
                    )
                );

                editor.keywordStats = stat as number[];
                editor.value = keywords as KeywordsData[];
                editor.input = getInputStringFromPhrasesList(editor.value, editor.view);

                break;
            }

            case KeywordsEditorActionType.DELETE_KEYWORD: {
                editor.value = editor.value.filter((v, idx) => idx !== action.keywordIndex);
                editor.keywordStats = editor.keywordStats.filter((v, idx) => idx !== action.keywordIndex);
                editor.input = getInputStringFromPhrasesList(editor.value, editor.view);

                break;
            }

            case KeywordsEditorActionType.SET_REFINE_KEYWORD_INDEX: {
                editor.refinedKeywordIndex = action.keywordIndex;

                break;
            }

            case KeywordsEditorActionType.REFINE_KEYWORD: {
                const keywordIndex = editor.refinedKeywordIndex;

                if (keywordIndex !== undefined) {
                    editor.keywordStats[keywordIndex] = action.phrase.shows;
                    editor.value[keywordIndex] = parseKeywordString(action.phrase.name);
                    editor.input = getInputStringFromPhrasesList(editor.value, editor.view);
                    editor.refinedKeywordIndex = undefined;
                }

                break;
            }

            case KeywordsEditorActionType.SET_ERRORS: {
                editor.errors = action.errors;

                break;
            }

            default:
                break;
        }
    });
};
